/**
 * Lead-source tracking utility.
 *
 * Captures UTM params, referrer, landing page, and current page silently
 * for every form submission. Persists landing/UTM data in sessionStorage
 * so the original entry context survives multi-page browsing.
 */

const STORAGE_KEY = "_3sm_tracking";

const KNOWN_SOURCES = {
  "instagram.com": "instagram",
  "instagr.am": "instagram",
  "l.instagram.com": "instagram",
  "tiktok.com": "tiktok",
  "vt.tiktok.com": "tiktok",
  "google.com": "google",
  "google.": "google", // catches google.co.uk, google.fr, etc.
  "facebook.com": "facebook",
  "fb.com": "facebook",
  "l.facebook.com": "facebook",
  "m.facebook.com": "facebook",
  "youtube.com": "youtube",
  "linkedin.com": "linkedin",
  "twitter.com": "twitter",
  "t.co": "twitter",
  "x.com": "twitter",
};

function detectFromUrl(host) {
  if (!host) return null;
  const h = host.toLowerCase();
  for (const [needle, label] of Object.entries(KNOWN_SOURCES)) {
    if (h.includes(needle)) return label;
  }
  return null;
}

function readPersisted() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePersisted(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

/**
 * Capture initial tracking context on first page load of the session.
 * Should be called once near app startup (idempotent).
 */
export function initTracking() {
  if (typeof window === "undefined") return;
  const existing = readPersisted();
  if (existing && existing.landing_page_url) return existing;

  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || "";

  let referrerHost = "";
  try {
    referrerHost = referrer ? new URL(referrer).hostname : "";
  } catch {
    referrerHost = "";
  }

  // Determine lead_source: utm_source > referrer host > direct
  const utmSource = (params.get("utm_source") || "").toLowerCase();
  let lead_source = "";
  if (utmSource) {
    lead_source = detectFromUrl(utmSource) || utmSource;
  } else if (referrerHost) {
    lead_source = detectFromUrl(referrerHost) || "website";
  } else {
    lead_source = "direct";
  }

  const data = {
    lead_source: lead_source || "unknown",
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_content: params.get("utm_content") || "",
    utm_term: params.get("utm_term") || "",
    referrer_url: referrer,
    landing_page_url: window.location.href,
  };

  writePersisted(data);
  return data;
}

/**
 * Get the full tracking payload to attach to a form submission.
 * Always includes the current page URL at submit time.
 */
export function getTrackingData() {
  if (typeof window === "undefined") {
    return {
      lead_source: "unknown",
      utm_source: "",
      utm_medium: "",
      utm_campaign: "",
      utm_content: "",
      utm_term: "",
      referrer_url: "",
      landing_page_url: "",
      current_page_url: "",
    };
  }
  const persisted = readPersisted() || initTracking();
  return {
    ...persisted,
    current_page_url: window.location.href,
  };
}
